/**
 * DICOM Basic Text SR writer (Sprint 5).
 *
 * Produces a Part-10 Explicit VR LE file that the viewer-api can
 * accept via a POST or forward to the scp-daemon via C-STORE. Only
 * the fields required for downstream systems (RIS / EMR) to display
 * the report are populated; full SR coding scheme (SNOMED-CT / LOINC)
 * is out of scope for Sprint 5 and is the subject of a dedicated
 * follow-up.
 *
 * The writer emits bytes, not DicomElements — the viewer posts these
 * straight at `/viewer-api/reports/{studyUid}/sr` which proxies to
 * scp-daemon.
 */

import type { Measurement } from "./types";

function encAscii(s: string): Uint8Array {
  return new TextEncoder().encode(s);
}

function pad(bytes: Uint8Array): Uint8Array {
  if (bytes.length % 2 === 0) return bytes;
  const out = new Uint8Array(bytes.length + 1);
  out.set(bytes);
  return out;
}

function elStr(group: number, element: number, vr: "UI" | "CS" | "DA" | "TM" | "SH" | "LO" | "PN" | "ST" | "UT", val: string): Uint8Array {
  const body = pad(encAscii(val));
  const out = new Uint8Array(8 + body.length);
  const dv = new DataView(out.buffer);
  dv.setUint16(0, group, true);
  dv.setUint16(2, element, true);
  out[4] = vr.charCodeAt(0);
  out[5] = vr.charCodeAt(1);
  dv.setUint16(6, body.length, true);
  out.set(body, 8);
  return out;
}

function elUS(group: number, element: number, v: number): Uint8Array {
  const out = new Uint8Array(10);
  const dv = new DataView(out.buffer);
  dv.setUint16(0, group, true);
  dv.setUint16(2, element, true);
  out[4] = "U".charCodeAt(0);
  out[5] = "S".charCodeAt(0);
  dv.setUint16(6, 2, true);
  dv.setUint16(8, v, true);
  return out;
}

function concat(parts: Uint8Array[]): Uint8Array {
  let n = 0;
  for (const p of parts) n += p.length;
  const out = new Uint8Array(n);
  let o = 0;
  for (const p of parts) { out.set(p, o); o += p.length; }
  return out;
}

function dicmPreamble(): Uint8Array {
  const out = new Uint8Array(132);
  out[128] = 0x44; out[129] = 0x49; out[130] = 0x43; out[131] = 0x4d;
  return out;
}

export interface SrContext {
  studyUid: string;
  mrn: string;
  patientName: string;
  text: string;  // free-form body the clinician wrote
  measurements: Measurement[];
}

/**
 * Compose a Basic Text SR (SOP 1.2.840.10008.5.1.4.1.1.88.11) as a
 * Part-10 Explicit VR LE byte array. The body text is inlined as a
 * single (0040,A040) container with a (0040,A160) text concept, and
 * every measurement is appended as a short line.
 */
export function writeBasicTextSR(ctx: SrContext): Uint8Array {
  const SOP_CLASS  = "1.2.840.10008.5.1.4.1.1.88.11";
  const SOP_INST   = crypto.randomUUID().replace(/-/g, "").slice(0, 32);
  const SERIES_UID = crypto.randomUUID().replace(/-/g, "").slice(0, 32);
  const TS         = "1.2.840.10008.1.2.1";

  const now = new Date();
  const da = now.toISOString().slice(0, 10).replace(/-/g, "");
  const tm = now.toTimeString().slice(0, 8).replace(/:/g, "");

  // Body — concept=CONTAINER plus a single TEXT child.
  const bodyText = [
    ctx.text,
    "",
    ...ctx.measurements.map((m) =>
      `[${m.kind}] ${m.label ?? ""} ${m.derivedMm?.toFixed(1) ?? ""} mm`,
    ),
  ].join("\n");

  const parts: Uint8Array[] = [
    dicmPreamble(),
    // File Meta group — minimal viable set.
    elStr(0x0002, 0x0002, "UI", SOP_CLASS),
    elStr(0x0002, 0x0003, "UI", SOP_INST),
    elStr(0x0002, 0x0010, "UI", TS),
    elStr(0x0002, 0x0012, "UI", "1.2.826.0.1.3680043.8.498.vaultpod"),
    // Dataset
    elStr(0x0008, 0x0016, "UI", SOP_CLASS),
    elStr(0x0008, 0x0018, "UI", SOP_INST),
    elStr(0x0008, 0x0020, "DA", da),
    elStr(0x0008, 0x0030, "TM", tm),
    elStr(0x0008, 0x0060, "CS", "SR"),
    elStr(0x0008, 0x0090, "PN", "VAULTPOD^VIEWER"),
    elStr(0x0010, 0x0010, "PN", ctx.patientName || "UNKNOWN"),
    elStr(0x0010, 0x0020, "LO", ctx.mrn || ""),
    elStr(0x0020, 0x000D, "UI", ctx.studyUid),
    elStr(0x0020, 0x000E, "UI", SERIES_UID),
    elStr(0x0020, 0x0010, "SH", "SR"),
    elStr(0x0020, 0x0011, "SH", "999"),
    elStr(0x0020, 0x0013, "SH", "1"),
    // SR-specific.
    elStr(0x0040, 0xA040, "CS", "TEXT"),
    elStr(0x0040, 0xA160, "UT", bodyText),
    elStr(0x0040, 0xA491, "CS", "COMPLETE"),
    elStr(0x0040, 0xA493, "CS", "FINAL"),
  ];
  return concat(parts);
}
